import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { isEmpty, isNil } from "lodash";

import TextButton from "~/components/buttons/TextButton";

import Breadcrumbs from "~/components/Breadcrumbs";
import ContentContainer from "~/components/layout/ContentContainer";
import SectionWrapper from "~/components/layout/SectionWrapper";

import ProductImageCarousel from "~/components/ProductImageCarousel";

import Row from "~/components/layout/Row";
import Col from "~/components/layout/Col";

import HeaderLinkGroup from "~/components/links/HeaderLinkGroup";
import TextLink from "~/components/links/TextLink";

import UnderlineTitle from "~/components/typography/UnderlineTitle";

import UserContent from "~/components/UserContent";

import { h1 } from "~/styles/typographyStyles";

import { sanitizeAndParseHTML } from "~/utilities/helpers";

import { useTranslationContext } from "~/context/TranslationContext";

const getTitleMargin = ({ titleMt, titleMb, noTitlePadding, showCarousel }) => {
    let top = titleMt;
    const right = `0`;
    let bottom = titleMb;
    const left = `0`;

    if (showCarousel) {
        bottom = `0.425em`;
    } else if (noTitlePadding) {
        bottom = `0`;
    }

    return `${top} ${right} ${bottom} ${left}`;
};

export const ContentWrapper = styled(SectionWrapper)`
    padding-bottom: ${({ theme }) => theme.spacing.medium};
`;

const Title = styled.h1`
    ${h1};
    margin: ${(props) => getTitleMargin(props)};
    font-weight: ${({ titleFontWgt }) => titleFontWgt};
`;

const Copy = styled(UserContent)`
    margin-bottom: ${({ theme }) => theme.layout.paragraphSpacing};
`;

const ReadMoreButton = styled(TextButton)`
    display: inline-block;
`;

const StyledTextLink = styled(TextLink)`
    font-weight: 700;
    margin-right: 15px;
`;

const PageHeader = ({
    copy = "",
    links = [],
    moreText = "",
    subtitle = "",
    title,
    sectionId = "",
    noMarginBottom = false,
    titleMt = "0",
    titleMb = "0",
    titleFontWgt = "auto",
    showBreadcrumbs = true,
    crumbs,
    lastCrumbLabel = "",
    textLinks = [],
    productImages = null,
    image = null,
    isProductCategoryPage = false,
}) => {
    const [showMore, setShowMore] = useState(false);
    const { activeCode } = useTranslationContext();

    const noTitlePadding = !copy && !moreText && !links;

    const showCarousel =
        isProductCategoryPage && !isNil(image) && productImages?.length > 0;

    return (
        <>
            {showBreadcrumbs && (
                <Breadcrumbs
                    backgroundColor="gray"
                    noMarginBottom
                    {...{ crumbs, lastCrumbLabel }}
                />
            )}
            <ContentWrapper
                backgroundColor="gray"
                withSmallerPadding
                {...{
                    sectionId,
                    noMarginBottom,
                }}
            >
                <ContentContainer>
                    {subtitle && !isProductCategoryPage ? (
                        <UnderlineTitle lang={activeCode} smallMarginBottom>
                            {subtitle}
                        </UnderlineTitle>
                    ) : null}
                    {!isProductCategoryPage ? (
                        <Title
                            lang={activeCode}
                            {...{
                                noTitlePadding,
                                titleMt,
                                titleMb,
                                titleFontWgt,
                                showCarousel,
                            }}
                        >
                            {title}
                        </Title>
                    ) : null}
                    <Row>
                        {showCarousel ? (
                            <Col md={1 / 4}>
                                <ProductImageCarousel
                                    {...{
                                        showCarousel,
                                        productImages,
                                    }}
                                />
                            </Col>
                        ) : null}
                        <Col md={showCarousel ? 3 / 4 : 1}>
                            {isProductCategoryPage ? (
                                <Title
                                    lang={activeCode}
                                    {...{
                                        noTitlePadding,
                                        titleMt,
                                        titleMb,
                                        titleFontWgt,
                                        showCarousel,
                                    }}
                                >
                                    {title}
                                </Title>
                            ) : null}
                            {copy ? (
                                <Copy lang={activeCode}>
                                    {sanitizeAndParseHTML(copy)}
                                </Copy>
                            ) : null}
                            {moreText ? (
                                <>
                                    {showMore ? (
                                        <Copy lang={activeCode}>
                                            {sanitizeAndParseHTML(moreText)}
                                        </Copy>
                                    ) : null}
                                    <ReadMoreButton
                                        onClick={() => setShowMore(!showMore)}
                                    >
                                        Read {showMore ? `Less` : `More`}
                                    </ReadMoreButton>
                                </>
                            ) : null}

                            {!isEmpty(links) ? (
                                <HeaderLinkGroup
                                    lang={activeCode}
                                    {...{ links }}
                                />
                            ) : null}

                            {!isEmpty(textLinks) ? (
                                <ContentContainer>
                                    {textLinks.map(
                                        ({ linkLabel, linkUrl }, index) => (
                                            <StyledTextLink
                                                key={index}
                                                to={linkUrl}
                                            >
                                                {linkLabel}
                                            </StyledTextLink>
                                        ),
                                    )}
                                </ContentContainer>
                            ) : null}
                        </Col>
                    </Row>
                </ContentContainer>
            </ContentWrapper>
        </>
    );
};

PageHeader.propTypes = {
    copy: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            action: PropTypes.oneOf(["link", "form", "video"]).isRequired,
            linkLabel: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]).isRequired,
            linkUrl: PropTypes.string,
            formId: PropTypes.number,
            isEmbed: PropTypes.bool,
            videoType: PropTypes.oneOf(["media", "download"]),
            downloadVideo: PropTypes.shape({}),
            video: PropTypes.shape({
                posterURL: PropTypes.string,
                mediaItemUrl: PropTypes.string,
            }),
            videoUrl: PropTypes.string,
        }),
    ),
    moreText: PropTypes.string,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    sectionId: PropTypes.string,
    noMarginBottom: PropTypes.bool,
    titleMt: PropTypes.string,
    titleMb: PropTypes.string,
    titleFontWgt: PropTypes.string,
    showBreadcrumbs: PropTypes.bool,
    crumbs: PropTypes.arrayOf(
        PropTypes.shape({
            crumbLabel: PropTypes.string.isRequired,
            pathname: PropTypes.string,
        }).isRequired,
    ).isRequired,
    lastCrumbLabel: PropTypes.string,
    textLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string,
            linkUrl: PropTypes.string,
        }),
    ),
    productImages: PropTypes.arrayOf(
        PropTypes.shape({
            altText: PropTypes.string,
            caption: PropTypes.string,
            showCaption: PropTypes.bool,
            image: PropTypes.shape({
                altText: PropTypes.string,
                caption: PropTypes.string,
                sourceUrl: PropTypes.string,
                title: PropTypes.string,
                slug: PropTypes.string,
            }),
        }),
    ),
    image: PropTypes.shape({}),
    isProductCategoryPage: PropTypes.bool,
};

export default PageHeader;
